import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  markCaptionsSessionStarted,
  unmarkCaptionsSessionStarted,
} from './callCaptionsSlice';
import { selectShouldRunCaptionsSession } from './selectors';

import type { RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsConnected } from 'features/call/call-ui-state/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { voiceSessionIdSelector } from 'features/voice-session/store';

interface CaptionsSessionParams {
  source: string;
}

// Shared ASR session for the voice meeting — one owner at a time (primary VCO
// sender / VI1 during remote teaming). Non-owners still update captionRequests
// locally but skip the start API; the owner starts when RNS or ownership change
// shows any requester still wants captions.
export const startCaptionsSessionThunk = createAsyncThunk(
  'callCaptions/startSession',
  async ({ source }: CaptionsSessionParams, { dispatch, getState }) => {
    const state = getState() as RootState;
    const voiceSessionId = voiceSessionIdSelector(state);
    const isCallConnected = selectIsConnected(state);
    const shouldRunCaptionsSession = selectShouldRunCaptionsSession(state);
    const isCaptionsSessionOwner = isPrimaryVcoSenderSelector(state);
    const alreadyStarted =
      state.callCaptions.startedSessionIds.includes(voiceSessionId);

    if (
      !shouldRunCaptionsSession ||
      !isCaptionsSessionOwner ||
      !isCallConnected ||
      !voiceSessionId ||
      alreadyStarted
    ) {
      return;
    }

    // Mark before the API so concurrent callers dedupe; unmark if start fails.
    dispatch(markCaptionsSessionStarted(voiceSessionId));

    dispatch(
      sendAnalyticsInfo({
        Method: 'startCaptionsSessionThunk',
        Message: 'Calling start-captions API',
        properties: { voiceSessionId, source },
      })
    );

    try {
      await VoiceSessionDaoService.startCaptions(voiceSessionId);
    } catch (error) {
      dispatch(unmarkCaptionsSessionStarted(voiceSessionId));
      console.warn('Voice captions failed to start', {
        error,
        voiceSessionId,
      });
      throw error;
    }
  }
);

export const stopCaptionsSessionThunk = createAsyncThunk(
  'callCaptions/stopSession',
  async ({ source }: CaptionsSessionParams, { dispatch, getState }) => {
    const state = getState() as RootState;
    const voiceSessionId = voiceSessionIdSelector(state);
    const shouldRunCaptionsSession = selectShouldRunCaptionsSession(state);
    const isCaptionsSessionOwner = isPrimaryVcoSenderSelector(state);

    if (!voiceSessionId) {
      return;
    }

    const wasStarted =
      state.callCaptions.startedSessionIds.includes(voiceSessionId);

    if (!wasStarted) {
      return;
    }

    // Multi-requester: only stop when nobody still wants captions, and only the
    // session owner may call the stop API (VI2 mirrors intent via RNS instead).
    if (shouldRunCaptionsSession || !isCaptionsSessionOwner) {
      return;
    }

    dispatch(unmarkCaptionsSessionStarted(voiceSessionId));

    dispatch(
      sendAnalyticsInfo({
        Method: 'stopCaptionsSessionThunk',
        Message: 'Calling stop-captions API',
        properties: { voiceSessionId, source },
      })
    );

    try {
      await VoiceSessionDaoService.stopCaptions(voiceSessionId);
    } catch (error) {
      console.warn('Voice captions failed to stop', {
        error,
        voiceSessionId,
      });
      throw error;
    }
  }
);
